import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { CompanyEntity } from "@/lib/types/company";
import { companyDetailActions } from "@/lib/actions/definitions/entities";

// ============================================================================
// COMPANY DETAIL PAGE CONFIGURATION
// ============================================================================

export const ENHANCED_COMPANY_DETAIL_CONFIG: DetailPageConfig<CompanyEntity> = {
  entity: "companies",
  displayName: "Company",

  // Tab configurations - organized with grouping for better visual hierarchy
  tabs: [
    // Core Information Group
    {
      id: "details",
      label: "Overview",
      icon: "building",
      variant: "blue",
      group: "core",
      priority: 1,
      clientVisible: true,
      content: {
        type: "custom",
        customComponent: "CompanyDetailsDisplay",
      },
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: "users",
      variant: "green",
      group: "core",
      priority: 2,
      content: {
        type: "custom",
        customComponent: "CompanyContactsDisplay",
      },
    },
    {
      id: "deals",
      label: "Deals",
      icon: "briefcase",
      variant: "blue",
      group: "core",
      priority: 3,
      content: {
        type: "custom",
        customComponent: "CompanyDealsDisplay",
      },
    },
    {
      id: "contracts",
      label: "Contracts",
      icon: "file-signature",
      variant: "blue",
      group: "core",
      priority: 3.5,
      content: {
        type: "custom",
        customComponent: "CompanyContractsDisplay",
      },
    },
    // Activity Group
    {
      id: "tasks",
      label: "Tasks",
      icon: "check-square",
      variant: "green",
      group: "activity",
      priority: 1,
      content: {
        type: "custom",
        customComponent: "CompanyTasksDisplay",
      },
    },
    {
      id: "history",
      label: "History",
      icon: "history",
      variant: "purple",
      group: "activity",
      priority: 1.5,
      // No `clientVisible`: a DELETE snapshot in an audit log can carry another
      // record's PII, so history is staff-only. The GET route enforces the same
      // thing with `isEmployeeRole` — the tab flag is not the security boundary.
      content: {
        type: "custom",
        customComponent: "CompanyHistoryDisplay",
      },
    },
    {
      id: "tickets",
      label: "Tickets",
      icon: "ticket",
      variant: "orange",
      group: "activity",
      priority: 2,
      clientVisible: true,
      content: {
        type: "custom",
        customComponent: "CompanyTicketsDisplay",
      },
    },
    {
      id: "time-entries",
      label: "Time Entries",
      icon: "clock",
      variant: "purple",
      group: "activity",
      priority: 2.5,
      clientVisible: true,
      content: {
        type: "custom",
        customComponent: "CompanyTimeEntriesDisplay",
      },
    },
    {
      // Client-facing, customer-safe MSP documentation. Marked clientVisible so
      // a client_user sees ONLY this curated documentation surface — never the
      // internal "Documentation" / "Drive" tabs, which expose internal Drive
      // contents and are intentionally NOT clientVisible.
      id: "client-documents",
      label: "Documents",
      icon: "book",
      variant: "blue",
      group: "resources",
      priority: 0.5,
      clientVisible: true,
      content: {
        type: "custom",
        customComponent: "CompanyClientDocumentsDisplay",
      },
    },
    {
      id: "recurring-transactions",
      label: "Recurring Transactions",
      icon: "repeat",
      variant: "blue",
      group: "activity",
      priority: 3,
      visible: (company) => {
        // Only visible to admins - component will handle role check
        return true;
      },
      content: {
        type: "custom",
        customComponent: "CompanyRecurringTransactionsDisplay",
      },
    },
    {
      id: "timeline",
      label: "Timeline",
      icon: "activity",
      variant: "purple",
      group: "activity",
      priority: 4,
      content: {
        type: "custom",
        customComponent: "CompanyTimelineDisplay",
      },
    },
    // Resources Group
    {
      id: "drive",
      label: "Drive",
      icon: "folder",
      variant: "blue",
      group: "resources",
      priority: 1,
      content: {
        type: "custom",
        customComponent: "CompanyDriveDisplay",
      },
    },
    {
      id: "support-link",
      label: "Support Link",
      icon: "link",
      variant: "blue",
      group: "resources",
      priority: 1.5,
      content: {
        type: "custom",
        customComponent: "CompanySupportLinkDisplay",
      },
    },
    {
      // Live, read-only support-group inbox (design doc §3.5). The component
      // handles its own enrolled/not-enrolled + employee-role states.
      id: "support-inbox",
      label: "Support Inbox",
      icon: "mail",
      variant: "blue",
      group: "activity",
      priority: 1.6,
      content: {
        type: "custom",
        customComponent: "CompanySupportInboxDisplay",
      },
    },
    {
      id: "documentation",
      label: "Documentation",
      icon: "book",
      variant: "purple",
      group: "resources",
      priority: 2,
      visible: (company) => {
        // Only show for admin/employee users - this will be handled by role check in the component
        return true; // The component itself will handle role-based visibility
      },
      content: {
        type: "custom",
        customComponent: "CompanyDocumentationDisplay",
      },
    },
    // Integration Group
    {
      id: "quickbooks",
      label: "QuickBooks",
      icon: "dollar-sign",
      variant: "green",
      group: "integration",
      priority: 1,
      visible: (company) => {
        // Only show for admin users - this will be handled by role check in the component
        return true; // The component itself will handle role-based visibility
      },
      content: {
        type: "custom",
        customComponent: "CompanyQuickBooksDisplay",
      },
    },
  ],

  // Action configurations - from centralized registry
  actions: companyDetailActions,

  // Breadcrumb configuration
  breadcrumbs: {
    show: true,
    items: [
      { label: "Companies", href: "/companies" },
      {
        label: "Company Details",
        href: (company: CompanyEntity) => `/companies/${company.id}`,
        current: true,
      },
    ],
  },

  // Page configuration
  defaultTab: "details",
  showHeader: true,
  showBreadcrumbs: true,
  showActions: true,

  // Title and description templates
  titleTemplate: (company: CompanyEntity) => company.name,
  descriptionTemplate: (company: CompanyEntity) => {
    const parts = [];

    // Add normalized name if available
    if (company.normalized_name) {
      parts.push(company.normalized_name);
    }

    return parts.length > 0 ? parts.join(" • ") : company.name;
  },
};
